import { memo, useMemo } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Link from '@base/Link/Link';
import Image from '@base/FutureImage';
import useWindowSize from '@hooks/useWindowSize';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { mediaQuery } from '@styles/index';
import useCompeteConfig from '@cont/Compete/useCompeteConfig';
import { getRedirectionURLOnLogoClick } from '@cont/NewPreSubscribed/utils';
import { useRouter } from 'next/router';
import { readCookie } from '@utils/cookies';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';
import {
  classRoomLogo,
  mobileLogo,
  MobileLogo,
  regularLogo,
  UnacademyLogo,
  unIconsLogo,
  UnWhiteLogo,
  withoutTextLogo
} from './constants';

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${({ $hasRightMargin }) => ($hasRightMargin ? '16px' : 0)};
`;

const UnIconsLogo = styled(Image)`
  ${({ $isFooter }) =>
    $isFooter
      ? `
        height: ${unIconsLogo.footer.height};
        width: ${unIconsLogo.footer.width};
      `
      : `height: ${unIconsLogo.main.height};
      width: ${unIconsLogo.main.width};
      ${mediaQuery.uptoTablet} {
        height: ${unIconsLogo.mobile.height};
        width: ${unIconsLogo.mobile.width};
      }
  `}
`;

const RegularLogoWrapper = styled.div`
  display: block;
  ${mediaQuery.uptoTablet} {
    ${(props) => (!props.$isFooter ? 'display: none;' : '')}
  }
`;

const RegularLogo = styled(Image)`
  height: ${({ $useCustomSize, $customHeight }) =>
    $useCustomSize ? `${$customHeight}px` : `${regularLogo.height}px`};
  width: ${({ $useCustomSize, $customWidth }) =>
    $useCustomSize ? `${$customWidth}px` : `${regularLogo.width}px`};
`;

const MobileLogoWrapper = styled.div`
  display: none;
  ${mediaQuery.uptoTablet} {
    ${(props) => (props.$isFooter ? 'display: none;' : 'display: block;')}
  }
`;

const StyledMobileLogo = styled(Image)`
  height: ${mobileLogo.height};
  width: ${mobileLogo.width};
`;

const PRIME_LOGO = {
  src: 'prime/prime-logo.svg',
  height: 40,
  width: 121
};

const Logo = ({
  gridColumn,
  disabled,
  withoutText,
  isClassroom,
  isUnIcons,
  isReload,
  className,
  type,
  isPrime,
  hasRightMargin
}) => {
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const { isTablet, isMobile } = useWindowSize();

  const router = useRouter();
  const {
    query: { goalUID, goalSlug }
  } = router;

  const { selectedGoal } = useSelectedGoal();

  // For prime pages, use selected goal if not in URL query
  const effectiveGoalUID = goalUID || (isPrime ? selectedGoal?.uid : null);
  const effectiveGoalSlug = goalSlug || (isPrime ? selectedGoal?.slug : null);

  const isFooter = type === 'footer';
  const isPrimeLogo = isPrime && !isFooter;
  const isPageReload = async (e) => {
    e.preventDefault();

    if (isPrime) {
      if (effectiveGoalUID && effectiveGoalSlug) {
        window.location.href = `/goal/${effectiveGoalSlug}/${effectiveGoalUID}/prime`;
      }
      return;
    }

    let url = '/';

    const selectedGoalUid = goalUID || readCookie('selectedGoalUid');

    if (selectedGoalUid) {
      const redirection = await getRedirectionURLOnLogoClick({
        goalUID: selectedGoalUid,
        isReload
      });

      url = redirection.as;
    }

    window.location.href = url;
  };
  const unIconsLogoProps = useMemo(() => {
    if (isUnIcons) {
      if ((isTablet || isMobile) && !isFooter) return unIconsLogo.mobile;
      if (isFooter) return unIconsLogo.footer;
      return unIconsLogo.main;
    }
    return {};
  }, [isMobile, isTablet, isUnIcons, isFooter]);

  const { showCompeteBackground } = useCompeteConfig();

  const regularLogoProps = useMemo(() => {
    if (isPrimeLogo) {
      return PRIME_LOGO;
    }
    return {
      src: isDarkModeOn ? UnWhiteLogo : UnacademyLogo,
      height: regularLogo.height,
      width: regularLogo.width
    };
  }, [isDarkModeOn, isPrimeLogo]);

  const mobileLogoProps = useMemo(
    () => ({
      src: MobileLogo,
      height: mobileLogo.height,
      width: mobileLogo.width
    }),
    []
  );

  const ClassroomLogo = useMemo(
    () => (
      <Image
        priority
        key={classRoomLogo.src}
        src={classRoomLogo.src}
        alt="Company Logo"
        data-analytics="global"
        className={className}
        $isFooter={isFooter}
        height={classRoomLogo.height}
        width={classRoomLogo.width}
      />
    ),
    [isFooter, className]
  );

  const WithoutTextLogo = useMemo(
    () => (
      <Image
        priority
        key={withoutTextLogo.src}
        src={withoutTextLogo.src}
        alt="Company Logo"
        data-analytics="global"
        className={className}
        $isFooter={isFooter}
        height={withoutTextLogo.height}
        width={withoutTextLogo.width}
      />
    ),
    [isFooter, className]
  );

  const UnIconsLogoComp = useMemo(
    () => (
      <UnIconsLogo
        priority
        key={unIconsLogoProps.src}
        src={unIconsLogoProps.src}
        alt="Company Logo"
        data-analytics="global"
        className={className}
        $isFooter={isFooter}
        height={unIconsLogoProps.height}
        width={unIconsLogoProps.width}
      />
    ),
    [unIconsLogoProps, className, isFooter]
  );

  const RegularLogoComp = useMemo(() => {
    const imageSrc =
      showCompeteBackground && !isFooter && !isPrimeLogo
        ? 'un_white_logo.svg'
        : regularLogoProps.src;

    return (
      <RegularLogoWrapper $isFooter={isFooter} $isPrime={isPrimeLogo}>
        <RegularLogo
          priority
          key={regularLogoProps.src}
          src={imageSrc}
          alt="Company Logo"
          data-analytics="global"
          className={className}
          height={regularLogoProps.height}
          width={regularLogoProps.width}
        />
      </RegularLogoWrapper>
    );
  }, [
    regularLogoProps,
    isFooter,
    isPrimeLogo,
    className,
    showCompeteBackground
  ]);

  const MobileLogoComp = useMemo(() => {
    const imageSrc =
      showCompeteBackground && !isFooter && !isPrime
        ? 'un_white_mobile_logo.svg'
        : mobileLogoProps.src;

    return (
      <MobileLogoWrapper $isFooter={isFooter}>
        <StyledMobileLogo
          key={mobileLogoProps.src}
          src={imageSrc}
          alt="Company Logo"
          data-analytics="global"
          className={className}
          height={mobileLogoProps.height}
          width={mobileLogoProps.width}
        />
      </MobileLogoWrapper>
    );
  }, [mobileLogoProps, className, isFooter, isPrime, showCompeteBackground]);

  const LogoComponent = () => (
    <LogoContainer $hasRightMargin={hasRightMargin}>
      {isClassroom && ClassroomLogo}
      {withoutText && WithoutTextLogo}
      {isUnIcons && UnIconsLogoComp}
      {!isClassroom && !withoutText && !isUnIcons && RegularLogoComp}
      {!isClassroom && !withoutText && !isUnIcons && !isPrime && MobileLogoComp}
    </LogoContainer>
  );

  const primeRedirection = isPrime && effectiveGoalUID && effectiveGoalSlug;
  const primeHref = primeRedirection
    ? '/goal/[goalSlug]/[goalUID]/prime'
    : '/prime';
  const primeAs = primeRedirection
    ? `/goal/${effectiveGoalSlug}/${effectiveGoalUID}/prime`
    : '/prime';

  return (
    <Link
      href={isPrime ? primeHref : '/'}
      as={isPrime ? primeAs : '/'}
      anchorStyle={`
        align-self: center;
        display: flex;
        align-items: center;
        height: ${() => {
          if (isPrimeLogo) {
            return `${PRIME_LOGO.height}px`;
          }

          if (isUnIcons) {
            return '36px';
          }

          return '24px';
        }};
        grid-column: ${gridColumn};
        pointer-events: ${disabled ? 'none' : 'auto'};
        ${
          !isFooter &&
          `${mediaQuery.uptoTablet} {
            grid-column: 1/2;
          }
        `
        }
      `}
      onClick={isPageReload}
      className={className}
    >
      <LogoComponent />
    </Link>
  );
};

export default memo(Logo);

Logo.propTypes = {
  gridColumn: PropTypes.string,
  disabled: PropTypes.bool,
  withoutText: PropTypes.bool,
  isClassroom: PropTypes.bool,
  isUnIcons: PropTypes.bool,
  isReload: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.string,
  isPrime: PropTypes.bool,
  hasRightMargin: PropTypes.bool
};

Logo.defaultProps = {
  gridColumn: '1/5',
  disabled: false,
  withoutText: false,
  isClassroom: false,
  isUnIcons: false,
  isReload: false,
  className: '',
  type: '',
  isPrime: false,
  hasRightMargin: true
};
